//  La recepción de mercadería (punto 7, migración 0044).
//
//  Un remito son varias líneas: `registrar_remito` las toma de una y le
//  imputa cada una a la orden que corresponda —o a ninguna—.
//
//  Las dos reglas que gobiernan esta pantalla son la D79 y la regla 5:
//
//  * **Lo que llegó entra, aunque no coincida** (D79). Recibir de más, de
//    menos, sin orden o con el catálogo caído nunca se frena desde acá: el
//    stock del sistema tiene que poder parecerse al del depósito. Lo que sí
//    se garantiza es que la diferencia quede escrita y a la vista.
//  * **Y si no se pudieron leer las órdenes, se dice** — no se muestra un
//    "este producto no tiene ninguna orden pendiente", que es exactamente
//    lo que lleva a cargar sin imputar mercadería que sí estaba pedida.

import { $, aviso, avisoHtml, el, esc, fecha, limpiar, noContesto, num, pedir, puede, rpc, rpcLee, sb, trabado, trabarSiNoContesto, pedirTodo } from './nucleo';
import { cargarStock } from './stock';

// ---------------------------------------------------------------------
//  El estado del módulo
// ---------------------------------------------------------------------
//  Las líneas del remito sin guardar viven acá y no en el DOM (regla 6): la
//  tabla se repinta entera con cada alta o baja.

type Linea = {
  producto_id: number; etiqueta: string; serializado: boolean;
  cantidad: number; series: string[];
  costo: number | null;
  orden_item_id: number | null; orden: string | null;
};

let LINEAS: Linea[] = [];
let ORDEN_ITEMS: any[] = [];
let RECEPCIONES: any[] = [];
let ERROR_ORDENES = '';
let ERROR_RECEPCIONES = '';

const NADA = '—';

// ---------------------------------------------------------------------
//  La carga
// ---------------------------------------------------------------------

export async function cargarRecepcion(){
  //  Cada tarjeta es una puerta: el formulario es del que puede registrar,
  //  el historial del que puede ver. El vendedor tiene `recepcion.ver` y
  //  no `recepcion.crear`, así que mira y no carga.
  $('#cardRemito').classList.toggle('oculto', !puede('recepcion','crear'));
  $('#cardRecepciones').classList.toggle('oculto', !puede('recepcion','ver'));
  pintarCampoCosto();
  pintarLineas();
  await Promise.all([cargarOrdenesPendientes(), cargarRecepciones(), cargarCostos()]);
}

async function cargarOrdenesPendientes(){
  ERROR_ORDENES = ''; ORDEN_ITEMS = [];
  if (!puede('recepcion','crear')){ pintarOrdenes(); return; }
  //  Sin `compra.ver`, `v_ordenes_pendientes` devuelve lista vacía con
  //  `error: null` — el RLS no avisa (regla 5). Se pregunta antes.
  if (!puede('compra','ver')){
    ERROR_ORDENES = 'No tenés permiso para ver las órdenes de compra, así que ' +
                    'no se puede imputar lo que llega contra una orden.';
    pintarOrdenes();
    return;
  }
  //  Enteras, las dos: con mil líneas pendientes, una que no llegó hace decir
  //  «este producto no tiene ninguna línea pendiente».
  const ord = await pedirTodo(() => sb.from('v_ordenes_pendientes').select('orden_id,numero,proveedor_id'),
    { por: 'orden_id' });
  if (ord.error){ ERROR_ORDENES = ord.error.message; pintarOrdenes(); return; }
  const ids = (ord.data || []).map((o: any) => o.orden_id);
  if (!ids.length){ pintarOrdenes(); return; }
  const it = await pedirTodo(() => sb.from('v_orden_items')
    .select('orden_item_id,orden_id,numero,producto_id,producto,pedido,recibido,pendiente')
    .in('orden_id', ids), { por: 'orden_item_id' });
  if (it.error){ ERROR_ORDENES = it.error.message; pintarOrdenes(); return; }
  const porOrden = new Map((ord.data || []).map((o: any) => [Number(o.orden_id), o]));
  ORDEN_ITEMS = (it.data || []).map((x: any) => ({
    ...x, proveedor_id: porOrden.get(Number(x.orden_id))?.proveedor_id ?? null }));
  pintarOrdenes();
}

async function cargarRecepciones(){
  ERROR_RECEPCIONES = ''; RECEPCIONES = [];
  if (!puede('recepcion','ver')){ pintarRecepciones(); return; }
  const { data, error } = await pedir(sb.from('v_recepciones').select('*')
    .order('recepcion_id', { ascending: false }).limit(30));
  if (error){ ERROR_RECEPCIONES = error.message; pintarRecepciones(); return; }
  RECEPCIONES = data || [];
  pintarRecepciones();
}

// ---------------------------------------------------------------------
//  Serie o cantidad
// ---------------------------------------------------------------------
//  La exporta `catalogo.ts`, que la llama cada vez que recarga la lista de
//  productos: el modo lo decide el producto elegido, no la persona.

export function modoSerie(){
  const o = $('#recProd').selectedOptions[0];
  const serie = o?.dataset.serie === 'true';
  $('#lblSeries').classList.toggle('oculto', !serie);
  $('#lblCant').classList.toggle('oculto', serie);
  contarSeries();
  pintarOrdenes();
}
$('#recProd').onchange = modoSerie;

function leerSeries(){
  return ($('#recSeries').value || '').split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
}
function contarSeries(){
  const n = leerSeries().length;
  $('#cuentaSeries').textContent = n ? `${n} unidad${n === 1 ? '' : 'es'} a dar de alta` : '';
}
$('#recSeries').oninput = contarSeries;

// ---------------------------------------------------------------------
//  Contra qué orden
// ---------------------------------------------------------------------
//  Se ofrecen las líneas abiertas del producto elegido y del proveedor
//  elegido — las mismas dos cosas que `registrar_remito` verifica—, así que
//  lo que la pantalla ofrece es lo que la base acepta.

function pintarOrdenes(){
  const prov = Number($('#recProv').value) || null;
  const prod = Number($('#recProd').value) || null;
  const sel = $('#recOrden');

  if (ERROR_ORDENES){
    //  Ni una lista vacía ni un "sin orden" a secas: si no se pudieron leer
    //  las órdenes hay que decirlo, porque imputar mal es silencioso.
    sel.innerHTML = '<option value="">— sin orden —</option>';
    sel.disabled = false;
    avisoHtml('#recOrdenMsg','warn',
      `No se pudieron leer las órdenes pendientes: ${esc(ERROR_ORDENES)}<br>` +
      '<b>Lo que llegó se puede cargar igual sin orden</b>, pero no va a quedar imputado.');
    //  El select volvió a «sin orden»: el campo del costo se repinta acá
    //  también (lo encontró la segunda ronda).
    pintarCampoCosto();
    return;
  }
  limpiar('#recOrdenMsg');

  const ops = ORDEN_ITEMS.filter(i =>
    (!prod || Number(i.producto_id) === prod) &&
    (!prov || Number(i.proveedor_id) === prov));
  sel.innerHTML = '<option value="">— sin orden —</option>' + ops.map(i =>
    `<option value="${i.orden_item_id}" data-orden="${esc(i.numero)}">` +
    `${esc(i.numero)} — faltan ${num(i.pendiente)} de ${num(i.pedido)}</option>`).join('');
  sel.disabled = false;
  if (!ops.length && prod)
    $('#recOrdenMsg').innerHTML =
      '<p class="hint">Este producto no tiene ninguna línea pendiente de este proveedor. ' +
      'Se puede recibir igual, sin orden.</p>';
  //  Las opciones se rehicieron y la elegida volvió a «sin orden».
  pintarCampoCosto();
}

//  EL CAMPO DEL COSTO (0053, D128). El costo del remito se CARGA por primera
//  vez: la base pide `costo.cargar` (recibir o ver costos no alcanza). Contra
//  UNA ORDEN pide además `costo.ver`: el costo del remito pisa el acordado y
//  la base no deja pisar lo que no se ve. Quien carga sin ver recibe sin costo
//  y lo completa después en «cargar el costo que faltaba».
const ofreceCosto = () =>
  puede('costo','cargar') && (!$('#recOrden').value || puede('costo','ver'));
function pintarCampoCosto(){
  $('#lblCosto').classList.toggle('oculto', !ofreceCosto());
}
$('#recOrden').onchange = pintarCampoCosto;

$('#recProv').onchange = () => {
  //  Cambiar de proveedor no borra lo cargado —sería perder trabajo— pero
  //  sí deja de imputar: una orden es de un proveedor, y la base rebota la
  //  línea entera si no coinciden. Mejor decirlo acá que allá.
  const imputadas = LINEAS.filter(l => l.orden_item_id != null);
  if (imputadas.length){
    imputadas.forEach(l => { l.orden_item_id = null; l.orden = null; });
    aviso('#recMsg','warn', imputadas.length === 1
      ? 'Cambiaste de proveedor: 1 línea quedó sin orden. Volvé a imputarla si corresponde.'
      : `Cambiaste de proveedor: ${imputadas.length} líneas quedaron sin orden. ` +
        'Volvé a imputarlas si corresponde.');
    pintarLineas();
  }
  pintarOrdenes();
};

// ---------------------------------------------------------------------
//  Las líneas del remito
// ---------------------------------------------------------------------

$('#btnRecAgregar').onclick = () => {
  //  `#recLineaMsg` y no `#recMsg`: agregar una línea no debe borrar el aviso
  //  "cambiaste de proveedor, N líneas quedaron sin orden".
  limpiar('#recLineaMsg');
  //  Y el informe del remito anterior no se queda abajo del que se está
  //  armando: dice "entró 3 de 10" sobre algo que ya se registró.
  limpiar('#recInforme');
  const opt = $('#recProd').selectedOptions[0];
  const prod = Number($('#recProd').value);
  if (!prod){ aviso('#recLineaMsg','bad','Elegí el producto.'); return; }
  const serializado = opt?.dataset.serie === 'true';

  const series = serializado ? leerSeries() : [];
  const cantidad = serializado ? series.length : Number($('#recCant').value || 0);
  if (serializado && !series.length){
    aviso('#recLineaMsg','bad','Este producto se maneja por número de serie: escaneá al menos uno.');
    return;
  }
  if (!serializado && (!Number.isInteger(cantidad) || cantidad <= 0)){
    aviso('#recLineaMsg','bad','La cantidad tiene que ser un número entero mayor a cero.');
    return;
  }

  const oOpt = $('#recOrden').selectedOptions[0];
  const orden_item_id = $('#recOrden').value ? Number($('#recOrden').value) : null;

  LINEAS.push({
    producto_id: prod,
    etiqueta: opt?.textContent || `#${prod}`,
    serializado, cantidad, series,
    costo: ofreceCosto() && $('#recCosto').value ? Number($('#recCosto').value) : null,
    orden_item_id,
    orden: orden_item_id ? (oOpt?.dataset.orden || null) : null,
  });

  $('#recSeries').value = ''; $('#recCant').value = '1'; $('#recCosto').value = '';
  contarSeries();
  pintarLineas();
};

function pintarLineas(){
  $('#tblRemito').innerHTML = LINEAS.map((l, i) => `
    <tr><td>${esc(l.etiqueta)}</td>
        <td class="num">${num(l.cantidad)}</td>
        <td>${l.serializado
          ? `<span class="mono hint">${esc(l.series.slice(0, 3).join(', '))}${
               l.series.length > 3 ? ` +${l.series.length - 3}` : ''}</span>`
          : '<span class="chip c-warn">por cantidad</span>'}</td>
        <td>${l.orden ? `<span class="chip c-pen mono">${esc(l.orden)}</span>`
                      : '<span class="hint">sin orden</span>'}</td>
        <td class="fila-acc"><button class="b b-sec quitarRec" data-i="${i}">Quitar</button></td></tr>
  `).join('') || '<tr><td colspan="5" class="vacio">Todavía no agregaste ninguna línea</td></tr>';
  const n = LINEAS.reduce((a, l) => a + l.cantidad, 0);
  $('#recCuenta').textContent = LINEAS.length
    ? `${LINEAS.length} línea${LINEAS.length === 1 ? '' : 's'}, ${num(n)} unidad${n === 1 ? '' : 'es'}`
    : '';
  $('#btnRec').disabled = LINEAS.length === 0 || trabado('#btnRec');
}

$('#tblRemito').onclick = e => {
  const b = el(e.target).closest('.quitarRec');
  if (!b) return;
  LINEAS.splice(Number(b.dataset.i), 1);
  pintarLineas();
};

// ---------------------------------------------------------------------
//  Registrar el remito
// ---------------------------------------------------------------------

$('#formRec').onsubmit = async e => {
  e.preventDefault();
  limpiar('#recMsg'); limpiar('#recInforme');
  if (!LINEAS.length){ aviso('#recMsg','bad','Agregá al menos una línea antes de registrar.'); return; }
  const prov = Number($('#recProv').value);
  if (!prov){ aviso('#recMsg','bad','Elegí el proveedor.'); return; }

  const btn = $('#btnRec'); btn.disabled = true;
  try {
    const { data, error } = await rpc('registrar_remito', {
      p_proveedor_id: prov,
      //  Ni `cantidad: null` ni `series: null`: la base los tolera, pero un
      //  null donde se puede mandar el dato deja la duda de si significa algo.
      //  La cantidad de una línea serializada es la cantidad de series.
      p_lineas: LINEAS.map(l => ({
        producto_id  : l.producto_id,
        cantidad     : l.cantidad,
        series       : l.series,
        costo        : l.costo,
        orden_item_id: l.orden_item_id,
      })) as any,
      p_remito: $('#recRemito').value.trim() || null,
      p_notas : $('#recNotas').value.trim() || null,
    });
    //  Un remito que no contestó quizá quedó registrado y repetirlo duplica el
    //  stock: el botón no vuelve hasta reentrar a la pestaña (D91, D92).
    trabarSiNoContesto('#btnRec', error);
    if (error){ aviso('#recMsg','bad', error.message); return; }

    //  El informe viene con `producto_id` y sin nombre: guardar los nombres
    //  ANTES de vaciar las líneas.
    const etiquetas: Record<number, string> = {};
    LINEAS.forEach(l => { etiquetas[l.producto_id] = l.etiqueta; });

    //  Recién con el remito adentro se olvida lo cargado.
    LINEAS = [];
    $('#recRemito').value = ''; $('#recNotas').value = '';
    $('#recSeries').value = ''; $('#recCosto').value = '';
    contarSeries(); pintarLineas();

    //  Primero se recarga, después se avisa. Los costos también: el remito
    //  nuevo trae líneas quizá sin costo.
    await Promise.all([cargarOrdenesPendientes(), cargarRecepciones(), cargarCostos()]);
    cargarStock();

    //  `avisoHtml` y no `aviso`: esto arma párrafos y una lista. Las series
    //  las tecleó una persona, así que van escapadas a mano.
    //  El plural va en TODAS las palabras, no sólo en el sustantivo
    //  ("1 unidad dadas de alta", "1 ya existían").
    const altas = Number(data?.altas ?? 0);
    let txt = `Recepción <b>#${esc(data?.recepcion_id)}</b> registrada: ` +
              (altas === 1 ? '1 unidad dada de alta.'
                           : `${num(altas)} unidades dadas de alta.`);
    const rep: string[] = data?.repetidas || [];
    if (rep.length){
      txt += '<br><br>' + (rep.length === 1
              ? '<b>1 ya existía</b> y se omitió: '
              : `<b>${rep.length} ya existían</b> y se omitieron: `) +
             `<span class="mono">${esc(rep.join(', '))}</span>`;
    }
    avisoHtml('#recMsg', rep.length ? 'warn' : 'ok', txt);
    pintarInforme(data?.lineas || [], etiquetas);
  } finally { btn.disabled = LINEAS.length === 0 || trabado('#btnRec'); }
};

//  El informe de diferencias, en el acto. La 0044 no le avisa a nadie más:
//  lo que no se muestra acá no lo ve nadie nunca. Ojo: lleva TODAS las
//  líneas, imputadas y sin imputar; no filtrar las que no tienen orden.
function pintarInforme(lineas: any[], productos: Record<number, string>){
  if (!lineas.length) return;
  const filas = lineas.map(l => {
    const quien = l.orden_item_id == null
      ? '<span class="hint">sin orden</span>'
      : `<span class="mono">${esc(l.orden)}</span>`;
    const dif = l.orden_item_id == null
      ? '<span class="chip c-warn">no se imputó</span>'
      : Number(l.excedente) > 0
        ? `<span class="chip c-bad">${num(l.excedente)} de más</span>`
        : Number(l.pendiente) > 0
          ? `<span class="chip c-warn">faltan ${num(l.pendiente)}</span>`
          : '<span class="chip c-ok">completa</span>';
    const celda = (v: any) => l.orden_item_id == null ? NADA : num(v);
    return `<tr><td>${esc(productos[Number(l.producto_id)] || `#${l.producto_id}`)}</td>
      <td>${quien}</td>
      <td class="num">${celda(l.pedido)}</td>
      <td class="num">${celda(l.recibido_antes)}</td>
      <td class="num"><b>${num(l.alta)}</b></td>
      <td class="num">${celda(l.recibido_total)}</td>
      <td>${dif}</td></tr>`;
  }).join('');
  const sueltas = lineas.filter(l => l.orden_item_id == null).length;
  $('#recInforme').innerHTML = `
    <h2>Qué entró, y contra qué</h2>
    ${sueltas ? `<p class="hint">${sueltas === 1
        ? 'Una línea entró sin imputarse a ninguna orden.'
        : `${sueltas} líneas entraron sin imputarse a ninguna orden.`}</p>` : ''}
    <div class="tw"><table>
      <thead><tr><th>Producto</th><th>Orden</th><th class="num">Pedido</th>
        <th class="num">Ya había</th><th class="num">Entró ahora</th>
        <th class="num">Total</th><th>Cómo queda</th></tr></thead>
      <tbody>${filas}</tbody></table></div>`;
}

// ---------------------------------------------------------------------
//  Las últimas recepciones
// ---------------------------------------------------------------------

function pintarRecepciones(){
  if (!puede('recepcion','ver')) return;
  if (ERROR_RECEPCIONES){
    $('#tblRecepciones').innerHTML =
      `<tr><td colspan="6" class="vacio">No se pudieron leer las recepciones: ${esc(ERROR_RECEPCIONES)}</td></tr>`;
    return;
  }
  $('#tblRecepciones').innerHTML = RECEPCIONES.map(r => `
    <tr><td>${fecha(r.ocurrido_en)}</td>
        <td>${esc(r.proveedor ?? NADA)}</td>
        <td class="mono">${esc(r.remito ?? NADA)}</td>
        <td class="num">${num(r.lineas)}</td>
        <td class="num">${num(r.unidades)}</td>
        <td>${r.ordenes
          ? `<span class="chip c-pen">${num(r.ordenes)}</span>`
          : '<span class="hint">sin orden</span>'}</td></tr>
  `).join('') || '<tr><td colspan="6" class="vacio">Todavía no se registró ninguna recepción</td></tr>';
}

// =====================================================================
//  LOS COSTOS DE LO RECIBIDO  (0051, D119)
// =====================================================================
//  Una línea de remito puede entrar sin costo y un costo cargado puede estar
//  mal: acá se completa y se corrige. Lo decide la base (D11, D67):
//
//  * CARGAR el que faltaba: `cargar_costo`, con `costo.cargar`.
//  * CORREGIR uno cargado: `corregir_costo`, con `costo.cargar` Y
//    `costo.ver` —no se corrige lo que no se ve— y con motivo obligatorio.
//    El motivo NO se valida acá: si falta, lo dice la base con su texto.
//  * El HISTORIAL de una línea: `historial_costo`, con `costo.ver`.
//
//  La lista sale de `lineas_recibidas`, porque `recepcion_items.costo_unitario`
//  está cerrada para la aplicación (D70). La abre `costo.cargar` O `costo.ver`;
//  sin `costo.ver` el monto viene null y la fila dice «tiene costo» (D114).
//
//  Los botones de cada fila sólo evitan ofrecer lo que la base rechazaría.

type LineaRecibida = {
  recepcion_item_id: number; recepcion_id: number; remito: string | null;
  proveedor: string | null; ocurrido_en: string; producto_id: number;
  codigo: string | null; producto: string | null; es_serializado: boolean;
  cantidad: number; tiene_costo: boolean; costo: number | null;
  cambios: number | null; total: number;
};

//  Lo tecleado y sin guardar vive ACÁ y no en el DOM (regla 6): la lista se
//  repinta entera. `modo` es el panel abierto debajo de la fila; hay uno solo.
type Abierta = {
  id: number; modo: 'cargar' | 'corregir' | 'historial';
  costo: string; motivo: string;
  //  El historial se lee al abrir el panel y se guarda con él, en sus tres
  //  estados: leyendo (null), leído (filas) y fallido (texto).
  hist: any[] | null; errorHist: string; codigoHist: string;
};

let COSTOS: LineaRecibida[] = [];
let ERROR_COSTOS = '', CODIGO_COSTOS = '';
let ESTADO_COSTOS: 'inicial' | 'leido' | 'error' = 'inicial';
let ABIERTA: Abierta | null = null;
//  Cada lectura lleva su número y sólo pinta la última: si la primera
//  contesta última, la tabla mostraría el filtro que ya no está puesto.
let LECTURA_COSTOS = 0;
//  El tope que se le pide a la base (ella lo acota entre 1 y 2000); siempre
//  se DICE cuando lo mostrado es menos que lo que hay (la regla de Equipos).
const TOPE_COSTOS = 200;

const puedeCargar = () => puede('costo','cargar');
const veCostos    = () => puede('costo','ver');

export async function cargarCostos(){
  const abre = puedeCargar() || veCostos();
  $('#cardCostos').classList.toggle('oculto', !abre);
  if (!abre){ COSTOS = []; ABIERTA = null; return; }
  //  La columna de correcciones es de quien ve costos: sin `costo.ver`
  //  `cambios` viene null, y guiones se leerían como «ninguna corrección».
  $('#thCcCambios').classList.toggle('oculto', !veCostos());

  const n = ++LECTURA_COSTOS;
  //  Mientras viaja la primera lectura se dice que está leyendo: vacía se
  //  leería como «no hay líneas».
  if (ESTADO_COSTOS === 'inicial') pintarCostos();
  const { data, error } = await rpcLee('lineas_recibidas', {
    p_solo_sin_costo: $('#ccSoloSin').checked, p_limite: TOPE_COSTOS });
  if (n !== LECTURA_COSTOS) return;
  if (error){
    //  Una lectura que falla no deja la lista vieja ni la vacía: dice que falló.
    COSTOS = []; ESTADO_COSTOS = 'error';
    ERROR_COSTOS = error.message; CODIGO_COSTOS = error.code || '';
  } else {
    COSTOS = ((data as unknown as LineaRecibida[]) || []);
    ESTADO_COSTOS = 'leido'; ERROR_COSTOS = ''; CODIGO_COSTOS = '';
  }
  //  Si la fila del panel abierto ya no está en la lista (filtro o tope), el
  //  panel se cierra con ella.
  if (ABIERTA && !COSTOS.some(c => c.recepcion_item_id === ABIERTA!.id)) ABIERTA = null;
  pintarCostos();
}

const COLS_COSTOS = () => veCostos() ? 8 : 7;

function pintarCostos(){
  const cols = COLS_COSTOS();
  const tope = $('#ccTope');
  tope.classList.add('oculto'); tope.textContent = '';

  if (ESTADO_COSTOS === 'inicial'){
    $('#tblCostos').innerHTML =
      `<tr><td colspan="${cols}" class="vacio">Leyendo las líneas recibidas…</td></tr>`;
    return;
  }
  if (ESTADO_COSTOS === 'error'){
    //  El 42501 es su propio estado: la base dijo que no (permiso o casilla
    //  sacados con la pantalla abierta).
    $('#tblCostos').innerHTML = `<tr><td colspan="${cols}" class="vacio">${
      CODIGO_COSTOS === '42501'
        ? 'La base no te deja ver las líneas recibidas: ' + esc(ERROR_COSTOS) +
          '<br>Si te cambiaron los permisos, recargá la página.'
        : 'No se pudieron leer las líneas recibidas: ' + esc(ERROR_COSTOS) +
          '<br>No es que no haya: no se sabe. Volvé a entrar a la pestaña para intentarlo de nuevo.'
      }</td></tr>`;
    return;
  }
  if (!COSTOS.length){
    //  Vacío DE VERDAD: la función no recorta por alcance, cero filas vale para todas.
    $('#tblCostos').innerHTML = `<tr><td colspan="${cols}" class="vacio">${
      $('#ccSoloSin').checked
        ? 'No hay ninguna línea sin costo: todo lo recibido tiene el costo cargado.'
        : 'Todavía no se recibió ninguna línea.'}</td></tr>`;
    return;
  }

  //  EL TOPE SE DICE. `total` es cuántas había antes del límite, y viene en
  //  cada fila.
  const total = Number(COSTOS[0].total ?? COSTOS.length);
  if (COSTOS.length < total){
    tope.textContent = `Se muestran las ${num(COSTOS.length)} más recientes de ${num(total)}.`;
    tope.classList.remove('oculto');
  }

  $('#tblCostos').innerHTML = COSTOS.map(c => {
    const abierta = ABIERTA && ABIERTA.id === c.recepcion_item_id ? ABIERTA : null;
    return filaCosto(c) + (abierta ? panelCosto(c, abierta, cols) : '');
  }).join('');
}

function celdaCosto(c: LineaRecibida){
  if (!c.tiene_costo) return '<span class="chip c-warn">sin costo</span>';
  //  Tiene costo y no vino el monto: es la base tapándolo porque no hay
  //  `costo.ver` (D114). NO es «sin costo» y NO es cero.
  if (c.costo == null) return '<span class="chip c-ok">tiene costo</span>';
  return `<b>${num(Number(c.costo))}</b>`;
}

function filaCosto(c: LineaRecibida){
  const id = c.recepcion_item_id;
  const botones: string[] = [];
  if (!c.tiene_costo && puedeCargar())
    botones.push(`<button class="b b-pri ccCargar" data-id="${id}">Cargar costo</button>`);
  if (c.tiene_costo && puedeCargar() && veCostos())
    botones.push(`<button class="b b-sec ccCorregir" data-id="${id}">Corregir</button>`);
  if (veCostos())
    botones.push(`<button class="b b-sec ccHist" data-id="${id}">Historial</button>`);
  return `<tr data-id="${id}"><td>${fecha(c.ocurrido_en)}</td>
    <td class="mono">${esc(c.remito ?? NADA)}</td>
    <td>${esc(c.proveedor ?? NADA)}</td>
    <td><span class="mono">${esc(c.codigo ?? '')}</span> ${esc(c.producto ?? NADA)}</td>
    <td class="num">${num(c.cantidad)}</td>
    <td>${celdaCosto(c)}</td>
    ${veCostos() ? `<td class="num">${c.cambios
        ? num(c.cambios) : '<span class="hint">ninguna</span>'}</td>` : ''}
    <td class="fila-acc">${botones.join(' ')}</td></tr>`;
}

//  El panel que se abre debajo de una fila. Los valores tecleados salen de
//  `ABIERTA`, así que sobreviven a cualquier repintado.
function panelCosto(c: LineaRecibida, a: Abierta, cols: number){
  let cuerpo = '';
  if (a.modo === 'cargar'){
    cuerpo = `
      <div class="grid g3">
        <label>Costo por unidad
          <input class="ccCosto" type="number" step="0.01" min="0" value="${esc(a.costo)}"
                 placeholder="0.00"></label>
      </div>
      <p class="hint">Se aplica a las ${num(c.cantidad)} unidades de esta línea, recalcula
         lo que ya se vendió de ellas y le carga la deuda al proveedor.</p>
      <div class="fila-acc" style="justify-content:flex-start">
        <button class="b b-pri ccOk" type="button">Guardar el costo</button>
        <button class="b b-sec ccCerrar" type="button">Cancelar</button>
      </div>`;
  } else if (a.modo === 'corregir'){
    cuerpo = `
      <div class="grid g2">
        <label>Costo nuevo por unidad
          <input class="ccCosto" type="number" step="0.01" min="0" value="${esc(a.costo)}"
                 placeholder="0.00"></label>
        <label>Motivo <span class="hint">obligatorio: es lo que se lee después en el historial</span>
          <input class="ccMotivo" value="${esc(a.motivo)}" placeholder="Por qué cambia"></label>
      </div>
      <p class="hint">Hoy es <b>${c.costo == null ? NADA : num(Number(c.costo))}</b>. Corregirlo
         recalcula lo que ya se vendió de esta línea y ajusta la cuenta del proveedor por
         la diferencia.</p>
      <div class="fila-acc" style="justify-content:flex-start">
        <button class="b b-pri ccOk" type="button">Guardar la corrección</button>
        <button class="b b-sec ccCerrar" type="button">Cancelar</button>
      </div>`;
  } else {
    cuerpo = historialHtml(a) +
      `<div class="fila-acc" style="justify-content:flex-start">
         <button class="b b-sec ccCerrar" type="button">Cerrar</button></div>`;
  }
  return `<tr class="ccPanel" data-id="${c.recepcion_item_id}"><td colspan="${cols}">
    ${cuerpo}<div class="ccPanelMsg"></div></td></tr>`;
}

function historialHtml(a: Abierta){
  if (a.errorHist)
    return `<div class="aviso a-bad">${a.codigoHist === '42501'
      ? 'La base no te deja ver el historial de costos: ' + esc(a.errorHist)
      : 'No se pudo leer el historial: ' + esc(a.errorHist) +
        '. No es que no tenga cambios: no se sabe.'}</div>`;
  if (a.hist === null) return '<p class="hint">Leyendo el historial…</p>';
  //  Sin filas no es «sin costo»: el del remito no pasa por `cargar_costo`.
  if (!a.hist.length)
    return '<p class="hint">Esta línea no tiene cargas ni correcciones de costo: el costo ' +
           'que tiene, si tiene, vino con el remito.</p>';
  return `<div class="tw"><table>
    <thead><tr><th>Cuándo</th><th>Qué</th><th class="num">Antes</th><th class="num">Después</th>
      <th>Motivo</th><th class="num">Recalculó</th><th>Quién</th></tr></thead>
    <tbody>${a.hist.map(h => `<tr>
      <td>${fecha(h.ocurrido_en)}</td>
      <td>${h.tipo === 'correccion' ? '<span class="chip c-warn">corrección</span>'
                                    : '<span class="chip c-ok">carga</span>'}</td>
      <td class="num">${h.costo_antes == null ? '<span class="hint">sin costo</span>'
                                              : num(Number(h.costo_antes))}</td>
      <td class="num"><b>${num(Number(h.costo_despues))}</b></td>
      <td>${esc(h.motivo ?? NADA)}</td>
      <td class="num">${num(h.unidades)} u. · ${num(h.items)} venta${Number(h.items) === 1 ? '' : 's'}</td>
      <td>${esc(h.quien ?? NADA)}</td></tr>`).join('')}</tbody></table></div>`;
}

async function abrirHistorial(id: number){
  ABIERTA = { id, modo: 'historial', costo: '', motivo: '', hist: null, errorHist: '', codigoHist: '' };
  const yo = ABIERTA;
  pintarCostos();
  const { data, error } = await rpcLee('historial_costo', { p_recepcion_item_id: id });
  //  Si mientras tanto se abrió otro panel, esta respuesta ya no es de nadie.
  if (ABIERTA !== yo) return;
  if (error){ yo.errorHist = error.message; yo.codigoHist = error.code || ''; }
  else yo.hist = (data as unknown as any[]) || [];
  pintarCostos();
}

$('#ccSoloSin').onchange = () => { limpiar('#ccMsg'); cargarCostos(); };

$('#tblCostos').onclick = async e => {
  const t = el(e.target);
  const id = Number(t.closest('[data-id]')?.dataset.id);
  if (t.closest('.ccCargar') || t.closest('.ccCorregir')){
    limpiar('#ccMsg');
    ABIERTA = { id, modo: t.closest('.ccCargar') ? 'cargar' : 'corregir',
                costo: '', motivo: '', hist: null, errorHist: '', codigoHist: '' };
    pintarCostos();
    $(`.ccPanel[data-id="${id}"] .ccCosto`)?.focus();
    return;
  }
  if (t.closest('.ccHist')){ limpiar('#ccMsg'); await abrirHistorial(id); return; }
  if (t.closest('.ccCerrar')){ ABIERTA = null; pintarCostos(); return; }
  if (t.closest('.ccOk')) await guardarCosto(t.closest('.ccOk'));
};

//  Lo tecleado va a `ABIERTA` en cada tecla (regla 6).
$('#tblCostos').oninput = e => {
  const t = el(e.target);
  if (!ABIERTA) return;
  if (t.classList.contains('ccCosto'))  ABIERTA.costo  = t.value;
  if (t.classList.contains('ccMotivo')) ABIERTA.motivo = t.value;
};

async function guardarCosto(btn: HTMLElement & { disabled: boolean }){
  const a = ABIERTA;
  if (!a || a.modo === 'historial') return;
  const panel = btn.closest('.ccPanel') as HTMLElement;
  const msg = $('.ccPanelMsg', panel);
  msg.innerHTML = '';
  //  Acá sólo se valida que haya un número; lo demás lo dice la base (D67).
  if (a.costo.trim() === '' || !Number.isFinite(Number(a.costo))){
    msg.innerHTML = '<div class="aviso a-bad">Escribí el costo por unidad.</div>';
    return;
  }
  btn.disabled = true;
  const costo = Number(a.costo);
  const { data, error } = a.modo === 'cargar'
    ? await rpc('cargar_costo', { p_recepcion_item_id: a.id, p_costo: costo })
    : await rpc('corregir_costo', { p_recepcion_item_id: a.id, p_costo: costo,
                                    p_motivo: a.motivo });
  if (error){
    //  Si la base no contestó, el botón NO vuelve: el costo quizá quedó
    //  guardado; reentrar a la pestaña relee la lista (D91).
    msg.innerHTML = `<div class="aviso a-bad">${esc(error.message)}</div>`;
    if (!noContesto(error)) btn.disabled = false;
    return;
  }
  //  Primero se recarga, después se avisa: si no, el repintado pisa el aviso.
  ABIERTA = null;
  await cargarCostos();
  aviso('#ccMsg','ok', data?.mensaje || 'Listo.');
}
